import { useEffect, useRef, type JSX } from 'react'
import { useAuthStore } from '../../../store/authStore.js'
import { apiGet } from '../../../api/client.js'

export interface EmailOAuthMessage {
  type: 'email-oauth-success' | 'email-oauth-error'
  provider: 'google' | 'microsoft'
  channel: 'inbound' | 'outbound'
  configId: string
  error?: string
}

export interface EmailOAuthSignInButtonsProps {
  configId?: string | undefined
  channel: 'inbound' | 'outbound'
  onOAuthComplete?: (result: EmailOAuthMessage) => void
}

/**
 * "Sign in with Google/Microsoft" buttons for the Email Inbound/Outbound
 * forms' OAuth auth mode. Opens the OAuth flow in a popup so the parent
 * page stays authenticated and receives the result via postMessage.
 */
export default function EmailOAuthSignInButtons(props: EmailOAuthSignInButtonsProps): JSX.Element {
  const tenantSlug = useAuthStore((s) => s.user?.tenantSlug)
  const popupRef = useRef<Window | null>(null)

  const oauthUrl = (provider: 'google' | 'microsoft'): string => {
    const params = new URLSearchParams({ channel: props.channel, tenantSlug: tenantSlug ?? '', popup: 'true' })
    if (props.configId && props.configId !== 'new') params.set('configId', props.configId)
    return `/api/admin/channels/email/oauth/${provider}?${params.toString()}`
  }

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      console.log('[EmailOAuth] message received', event.origin, event.data, event.source === popupRef.current)
      const data = event.data as EmailOAuthMessage | undefined
      if (data?.type === 'email-oauth-success' && data.channel === props.channel && data.configId) {
        try {
          const source = event.source as Window | undefined
          if (source && typeof source.close === 'function') {
            source.close()
          }
        } catch (e) {
          console.warn('[EmailOAuth] could not close via event.source', e)
        }
        popupRef.current?.close()
        props.onOAuthComplete?.(data)
      }
    }
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [props.channel, props.onOAuthComplete])

  const openOAuth = async (provider: 'google' | 'microsoft') => {
    // Open the popup synchronously (inside the click) so it is not blocked, then make sure the
    // session cookie is fresh before pointing it at the OAuth start route, which now requires
    // a signed-in admin. A cheap authenticated call refreshes an expired access token silently.
    const popup = window.open('about:blank', 'email-oauth-popup', 'popup=true,width=500,height=600')
    popupRef.current = popup
    try {
      await apiGet('/auth/me')
    } catch {
      // Session is gone; the start route will answer 401 in the popup.
    }
    if (popup && !popup.closed) popup.location.href = oauthUrl(provider)
  }

  return (
    <div className="flex gap-3">
      <button
        type="button"
        onClick={() => void openOAuth('google')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          backgroundColor: '#4285F4',
          color: '#fff',
          border: 'none',
          borderRadius: '4px',
          padding: '0',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 500,
          fontFamily: 'Roboto, sans-serif',
          height: '40px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        }}
      >
        <span style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#fff',
          borderRadius: '2px',
          width: '38px',
          height: '38px',
          margin: '1px',
        }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" width="20" height="20">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6,4.51-4.18 7.09-10.36 7.09-17.65z" />
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
          </svg>
        </span>
        <span style={{ paddingRight: '12px' }}>Sign in with Google</span>
      </button>

      <button
        type="button"
        onClick={() => void openOAuth('microsoft')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          backgroundColor: '#2F2F2F',
          color: '#fff',
          border: 'none',
          borderRadius: '4px',
          padding: '0',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 600,
          fontFamily: '"Segoe UI", sans-serif',
          height: '40px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.4)',
        }}
      >
        <span style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '38px',
          height: '38px',
          margin: '1px',
        }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 21 21" width="20" height="20">
            <rect x="1" y="1" width="9" height="9" fill="#F25022" />
            <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
            <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
            <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
          </svg>
        </span>
        <span style={{ paddingRight: '12px' }}>Sign in with Microsoft</span>
      </button>
    </div>
  )
}
